import { LEGAL } from "@/lib/legal";
import { LegalLayout, Section, Bullets } from "./LegalLayout";

/**
 * Privacy policy.
 *
 * Google Play requires a policy URL for every listing and checks that it
 * describes the data the app actually handles. The sections below are written
 * against the real code paths, so if a data flow changes the matching paragraph
 * has to change with it:
 *
 *   account email + password hash   POST /api/auth/register, users_collection
 *   library (history/saved/follows) POST /api/me/library/merge, localStorage
 *   digest email                    POST /api/subscribe, subscribers_collection
 *   outbound click                  GET  /api/r/{video_id}, clicks_collection
 *   "watching now" heartbeat        POST /api/activity/heartbeat (hashed IP)
 *   product analytics               PostHog, loaded in public/index.html
 *
 * The Data safety form in Play Console must be filled in to match this page.
 */
export const PrivacyPolicyPage = () => (
  <LegalLayout
    title="Privacy Policy"
    subtitle={`How ${LEGAL.appName} handles your data.`}
  >
    <p>
      {LEGAL.appName} ("we", "us") shows what is trending on public video
      platforms. This policy explains exactly what we collect, why, who it is
      shared with, how long it is kept, and how to delete it. It applies to the{" "}
      {LEGAL.appName} mobile app, the desktop app and {LEGAL.site}.
    </p>

    <Section heading="The short version">
      <Bullets
        items={[
          "You can use the app without an account. Browsing, watch history, saves and follows all work on your device with nothing sent to us.",
          "An account is optional and exists only to sync that library between your devices. It needs an email address and a password.",
          "We do not sell your data, and we do not share it with advertisers or data brokers.",
          "You can delete your account and everything stored under it at any time, from inside the app or from the web, with no email exchange required.",
        ]}
      />
    </Section>

    <Section heading="Information we collect">
      <p className="font-semibold text-white/90">Account information (only if you create an account)</p>
      <Bullets
        items={[
          "Your email address, used as your sign-in identifier and to contact you about your account.",
          "Your password, stored only as a salted one-way hash. We never store or have access to the password itself.",
          "The date the account was created and the date you last signed in.",
        ]}
      />

      <p className="font-semibold text-white/90 pt-2">Your library</p>
      <Bullets
        items={[
          "Videos you have watched in the app, videos you have saved, and channels you follow.",
          "This is stored on your device. It is uploaded to our servers and linked to your account only when you are signed in, so it can be restored on your other devices.",
          "Signed out, it never leaves your device.",
        ]}
      />

      <p className="font-semibold text-white/90 pt-2">Usage and device information</p>
      <Bullets
        items={[
          "When you open a video on the source platform, we record which video was opened, the trending country, the content type and the time. This is used to rank what is trending. It is not linked to your account.",
          "While a video is playing we send a periodic 'still watching' signal so the app can show live viewer counts. Your IP address is turned into an irreversible hash before it is stored and is not kept in raw form; the record is discarded within about 30 seconds.",
          "Our servers keep standard request logs, which include IP address and user agent, for security, abuse prevention and debugging.",
          "We use PostHog for product analytics to understand which screens are used and to diagnose errors. This includes a randomly generated analytics identifier, approximate location derived from IP, device and app version, and screens visited. Where session replay is enabled, it also records interactions with the app interface.",
        ]}
      />

      <p className="font-semibold text-white/90 pt-2">Email digest (only if you sign up for it)</p>
      <Bullets
        items={[
          "The email address you enter and the country you selected, kept until you unsubscribe.",
        ]}
      />

      <p className="pt-2">
        We do not collect your name, phone number, contacts, precise location,
        photos, files, microphone or camera data. The app requests only internet
        access.
      </p>
    </Section>

    <Section heading="Why we use it">
      <Bullets
        items={[
          "To provide the service: showing trending videos, and syncing your library when you are signed in.",
          "To rank content: aggregated, non-identifying click and view counts are what produce the trending, rising and leaderboard rankings.",
          "To keep the service working and safe: rate limiting, abuse prevention, error diagnosis and security investigation.",
          "To improve the product: aggregated analytics about which features are used.",
        ]}
      />
      <p>
        We do not use your data for advertising, profiling for advertising, or
        automated decisions that affect you legally or similarly significantly.
      </p>
    </Section>

    <Section heading="Who we share it with">
      <p>
        We do not sell personal data and we do not share it with advertisers. We
        share limited data only with service providers who process it on our
        behalf, under contract:
      </p>
      <Bullets
        items={[
          "Our hosting and database provider, which stores the data described above.",
          "PostHog, our product analytics provider.",
        ]}
      />
      <p>
        We may disclose data if we are legally required to do so, or where it is
        necessary to protect our rights, users or the security of the service.
      </p>
    </Section>

    <Section heading="Third-party video platforms">
      <p>
        {LEGAL.appName} links to and embeds publicly available videos using the
        official embedded players of the source platforms, including YouTube.
        When a video plays or you open it on the source platform, that platform
        receives your request directly and its own privacy policy applies to it.
        We are not affiliated with, endorsed by or sponsored by YouTube, Google
        LLC or any other platform we index.
      </p>
      <Bullets
        items={[
          <>
            YouTube Terms of Service:{" "}
            <a
              className="text-orange-400 hover:text-orange-300"
              href="https://www.youtube.com/t/terms"
              target="_blank"
              rel="noreferrer noopener"
            >
              youtube.com/t/terms
            </a>
          </>,
          <>
            Google Privacy Policy:{" "}
            <a
              className="text-orange-400 hover:text-orange-300"
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noreferrer noopener"
            >
              policies.google.com/privacy
            </a>
          </>,
        ]}
      />
    </Section>

    <Section heading="How long we keep it">
      <Bullets
        items={[
          "Account data and your synced library: until you delete your account, after which it is removed from our live database immediately.",
          "Live 'watching now' records: about 30 seconds.",
          "Click records used for rankings: retained in aggregate; they are not linked to your account.",
          "Server request logs: a short rolling window for security and debugging.",
          "Digest subscriptions: until you unsubscribe.",
          "Backups may retain deleted data for a short period before being overwritten on their normal cycle.",
        ]}
      />
    </Section>

    <Section heading="Your rights and choices">
      <Bullets
        items={[
          "Access and export: ask us for a copy of the data held under your account.",
          "Correction: your email address can be changed by contacting us.",
          "Deletion: delete your account and its data yourself, in the app under Account, or from the web at /delete-account. No account is needed to keep using the app.",
          "Unsubscribe: opt out of the digest at any time from the unsubscribe option or by contacting us.",
          "Analytics: you can opt out of analytics by contacting us at the address below.",
          "Depending on where you live, you may also have rights under the GDPR, the UK GDPR, the CCPA/CPRA or India's DPDP Act, including the right to object to processing and to lodge a complaint with your data protection authority.",
        ]}
      />
      <p>
        Where the GDPR applies, our legal bases are: performance of a contract
        (providing the account and sync you asked for), consent (the email
        digest and analytics), and legitimate interests (keeping the service
        secure and working, and producing aggregate rankings).
      </p>
    </Section>

    <Section heading="Children">
      <p>
        {LEGAL.appName} is not directed at children under 13 (or the minimum age
        of digital consent where you live, if higher), and we do not knowingly
        collect data from them. If you believe a child has given us data, contact
        us and we will delete it.
      </p>
    </Section>

    <Section heading="Security">
      <p>
        Traffic between the app and our servers is encrypted over HTTPS.
        Passwords are stored only as salted hashes. Sign-in attempts are rate
        limited. No system is perfectly secure, but we take reasonable technical
        and organisational measures to protect your data.
      </p>
    </Section>

    <Section heading="International transfers">
      <p>
        Our servers and service providers may process data in countries other
        than yours. Where required, we rely on appropriate safeguards such as
        the European Commission's standard contractual clauses.
      </p>
    </Section>

    <Section heading="Changes to this policy">
      <p>
        We will update this page and change the "last updated" date when this
        policy changes. Material changes will be announced in the app.
      </p>
    </Section>

    <Section heading="Contact">
      <p>
        Privacy questions and data requests:{" "}
        <a className="text-orange-400 hover:text-orange-300" href={`mailto:${LEGAL.privacyEmail}`}>
          {LEGAL.privacyEmail}
        </a>
        <br />
        General support:{" "}
        <a className="text-orange-400 hover:text-orange-300" href={`mailto:${LEGAL.supportEmail}`}>
          {LEGAL.supportEmail}
        </a>
        <br />
        Data controller: {LEGAL.entity}, {LEGAL.jurisdiction}.
      </p>
    </Section>
  </LegalLayout>
);

export default PrivacyPolicyPage;
